import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

const confirmSubscription = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ token: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: sub, error } = await supabaseAdmin
      .from("subscribers")
      .select("id, email, confirmed, confirmed_at")
      .eq("confirm_token", data.token)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!sub) return { ok: false as const, reason: "invalid" as const };
    if (sub.confirmed) return { ok: true as const, already: true as const };
    const { error: updateError } = await supabaseAdmin
      .from("subscribers")
      .update({ confirmed: true, confirmed_at: new Date().toISOString() })
      .eq("id", sub.id);
    if (updateError) throw new Error(updateError.message);
    return { ok: true as const, already: false as const };
  });

export const Route = createFileRoute("/tilmeld/bekraeft")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  head: () => ({
    meta: [
      { title: "Bekræft tilmelding – Flâneriet" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ConfirmPage,
});

function ConfirmPage() {
  const { token } = Route.useSearch();
  const confirmFn = useServerFn(confirmSubscription);

  const q = useQuery({
    queryKey: ["confirm-subscription", token],
    queryFn: () => confirmFn({ data: { token } }),
    enabled: Boolean(token),
    retry: false,
  });

  return (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <p className="eyebrow">Tilmelding</p>
      {!token ? (
        <>
          <h1 className="mt-4 font-serif text-3xl">Manglende bekræftelse</h1>
          <p className="mt-4 meta italic">
            Der mangler et bekræftelses-link. Skriv dig op igen fra forsiden.
          </p>
        </>
      ) : q.isLoading ? (
        <>
          <h1 className="mt-4 font-serif text-3xl">Et øjeblik…</h1>
          <p className="mt-4 meta italic">Bekræfter din tilmelding.</p>
        </>
      ) : q.isError || (q.data && !q.data.ok) ? (
        <>
          <h1 className="mt-4 font-serif text-3xl">Linket kunne ikke bruges</h1>
          <p className="mt-4 meta italic">
            Måske er det allerede brugt, eller også er det udløbet. Prøv at
            skrive dig op igen fra forsiden.
          </p>
          <a href="/" className="mt-8 inline-block border-b border-bordeaux pb-0.5 text-bordeaux">
            Til forsiden
          </a>
        </>
      ) : q.data?.already ? (
        <>
          <h1 className="mt-4 font-serif text-3xl">Du er allerede skrevet op</h1>
          <p className="mt-4 meta italic">
            Din adresse er bekræftet. Du hører fra mig, når der er noget værd at læse.
          </p>
          <a href="/" className="mt-8 inline-block border-b border-bordeaux pb-0.5 text-bordeaux">
            Til forsiden
          </a>
        </>
      ) : (
        <>
          <h1 className="mt-4 font-serif text-3xl">Tak. Din tilmelding er bekræftet.</h1>
          <p className="mt-4 meta italic">
            Du får et stille brev de dage, hvor Flâneriet har fundet nyt. Ingen
            reklamer, ingen sporing.
          </p>
          <a href="/" className="mt-8 inline-block border-b border-bordeaux pb-0.5 text-bordeaux">
            Til forsiden
          </a>
        </>
      )}
    </div>
  );
}
